/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { Moment } from "moment";
import { trace } from "owa-trace";
import ShiftRequestUtils, { ShiftRequestStatus } from "sh-application/utility/ShiftRequestUtils";
import {
    IApproveDeclineTimeOffRequestResponseEntity,
    IApproveSwapHandOffRequestResponseEntity,
    IBulkOpenShiftRequestsResponseEntity,
    ICreateTimeOffResponseEntity,
    IGetShiftRequestsResponseEntity,
    IOpenShiftEntity,
    IShiftEntity,
    IShiftRequestAndUnreadCountResponseEntity,
    IShiftRequestEntity,
    IShiftRequestResponseEntity,
    IUserTimeOffReasonsReponseEntity,
    ShiftRequestEntity,
    ShiftRequestType
} from "sh-models";
import { setMemberShiftsInStore } from "sh-requestconflict-store";
import RestClient from "sh-rest-client";
import { DataProcessingHelpers, ECSConfigKey, ECSConfigService, InstrumentationService, ShiftDataService, TeamDataService } from "sh-services";
import {
    shiftRequestStore,
    updateRequestsNextCursorInStore,
    updateShiftRequestsInStore,
    updateUnreadRequestsCountInStore
} from "sh-shiftrequests-store";
import { setAreAllActiveRequestsLoadedInStore } from "sh-stores/sh-shiftrequests-store";
import { TeamStore } from "sh-team-store";

import { DataService } from "./DataService";
import IShiftRequestDataService from "./IShiftRequestDataService";

class ShiftRequestDataService extends DataService implements IShiftRequestDataService {
    /**
     * Delete shift request action - Cancels the shift request and updates the store
     * @param shiftRequest
     */
    public async deleteShiftRequest(shiftRequest: IShiftRequestEntity): Promise<IShiftRequestEntity> {
        let marker = "deleteRequest";
        marker = InstrumentationService.perfMarkerStart(marker);

        try {
            const deletedShiftRequest: IShiftRequestEntity = await RestClient.deleteRequest(
                shiftRequest.tenantId,
                shiftRequest.teamId,
                shiftRequest.id
            );
            // Delete sets the state to cancelled, doesn't delete the entity. Should be handled same as update
            updateShiftRequestsInStore([deletedShiftRequest]);
            return deletedShiftRequest;
        } finally {
            InstrumentationService.perfMarkerEnd(marker);
        }
    }

    /**
     * Get ShiftRequests associated with a shiftId
     * @param tenantId
     * @param teamId
     * @param shiftId
     */
    public async getShiftRequestsForShiftId(teamId: string, shiftId: string): Promise<IShiftRequestEntity[]> {
        if (!shiftRequestStore().areAllActiveRequestsLoaded) {
            // If the active requests are not loaded into the store, load them first
            await this.getShiftRequestsForTeam(this.tenantId, teamId);
        }

        const existingShiftRequests: IShiftRequestEntity[] = [];
        shiftRequestStore().shiftRequests.forEach((shiftRequest: IShiftRequestEntity) => {
            if (
                shiftRequest.shiftId === shiftId &&
                ShiftRequestUtils.getShiftRequestStatus(shiftRequest) == ShiftRequestStatus.InProgress
            ) {
                existingShiftRequests.push(shiftRequest);
            }
        });

        return existingShiftRequests;
    }

    /**
     * Returns a specific shift request from the local cache or network. Returns null if no shift with that id
     * is found.
     * @param tenantId - team tenant id
     * @param teamId - team id
     * @param shiftRequestId - shift request id
     */
    public async getShiftRequestById(tenantId: string, teamId: string, shiftRequestId: string): Promise<IShiftRequestEntity> {
        let marker = "getShiftRequestById";
        marker = InstrumentationService.perfMarkerStart(marker);

        try {
            if (shiftRequestStore().shiftRequests.has(shiftRequestId)) {
                return Promise.resolve(shiftRequestStore().shiftRequests.get(shiftRequestId));
            } else {
                const shiftRequest = await RestClient.getShiftRequest(tenantId, teamId, shiftRequestId);
                if (shiftRequest) {
                    updateShiftRequestsInStore([shiftRequest]);
                }

                return shiftRequest;
            }
        } catch (error) {
            return null;
        } finally {
            InstrumentationService.perfMarkerEnd(marker);
        }
    }

    /**
     * Get ShiftRequests from service
     * @param tenantId
     * @param teamId
     * @param cursor - cursor to fetch the next page
     */
    public async getShiftRequestsForTeam(tenantId: string, teamId: string, cursor?: string): Promise<IShiftRequestEntity[]> {
        let marker = "getShiftRequestsForTeam";
        marker = InstrumentationService.perfMarkerStart(marker);

        if (!shiftRequestStore().areAllActiveRequestsLoaded && cursor) {
            trace.error("Should not have a shiftRequests cursor if the active requests have not been loaded");
        }
        const fetchingActiveRequests: boolean = !cursor;

        try {
            // When fetching all active requests, don't fetch the data again if they have been loaded but rather return them directly from the store.
            // Likewise, when fetching a single page of requests and it is the first page, don't fetch the data again if it have been loaded but rather return it directly from the store.
            if (fetchingActiveRequests && shiftRequestStore().areAllActiveRequestsLoaded) {
                return Promise.resolve(DataProcessingHelpers.getArrayFromMap(shiftRequestStore().shiftRequests));
            }

            const getShiftRequestsResponse: IGetShiftRequestsResponseEntity = await RestClient.getShiftRequests(tenantId, teamId, cursor);
            if (getShiftRequestsResponse) {
                updateShiftRequestsInStore(getShiftRequestsResponse.shiftRequests);
                updateUnreadRequestsCountInStore(getShiftRequestsResponse.unreadShiftRequests);
                updateRequestsNextCursorInStore(getShiftRequestsResponse.nextCursor);

                // Track if we load have loaded the first request which contains all the active requests
                // this will make sure that we don't reload the same data when opening the shift requests panel
                if (fetchingActiveRequests && !shiftRequestStore().areAllActiveRequestsLoaded) {
                    // all the active requests come in the first request
                    setAreAllActiveRequestsLoadedInStore(true);
                }
            } else {
                // Ideally shouldn't happen
                updateUnreadRequestsCountInStore(0);
                updateRequestsNextCursorInStore("");
            }
            return getShiftRequestsResponse.shiftRequests;
        } finally {
            InstrumentationService.perfMarkerEnd(marker);
        }
    }

    /**
     * Set shiftRequestReadStatus for a shift request
     */
    public async setShiftRequestReadStatus(
        tenantId: string,
        teamId: string,
        shiftRequestId: string,
        shiftRequestReadTime: Moment
    ): Promise<IShiftRequestEntity> {
        let marker = "setShiftRequestReadStatus";
        marker = InstrumentationService.perfMarkerStart(marker);
        try {
            const response: IShiftRequestAndUnreadCountResponseEntity = await RestClient.setShiftRequestRead(
                tenantId,
                teamId,
                shiftRequestId,
                shiftRequestReadTime
            );
            let updatedShiftRequest: IShiftRequestEntity = null;
            if (response && response.shiftRequest) {
                updatedShiftRequest = response.shiftRequest;
                updateShiftRequestsInStore([updatedShiftRequest]);
            }
            updateUnreadRequestsCountInStore(response.unreadShiftRequests);
            return updatedShiftRequest;
        } finally {
            InstrumentationService.perfMarkerEnd(marker);
        }
    }

    /**
     * Set shiftRequestReadStatus for bulk shift requests in parallel
     */
    public async setShiftRequestReadStatusBulk(
        tenantId: string,
        teamId: string,
        shiftRequests: IShiftRequestEntity[]
    ): Promise<IShiftRequestEntity[]> {
        let marker = "setShiftRequestReadStatusBulk";
        marker = InstrumentationService.perfMarkerStart(marker);
        try {
            const promises: Array<Promise<IShiftRequestEntity>> = new Array<Promise<IShiftRequestEntity>>();
            shiftRequests.forEach(openShiftRequest => {
                if (openShiftRequest && !ShiftRequestUtils.isRequestReadByMe(openShiftRequest)) {
                    promises.push(this.setShiftRequestReadStatus(tenantId, teamId, openShiftRequest.id, openShiftRequest.lastModifiedTime));
                }
            });
            return Promise.all(promises);
        } finally {
            InstrumentationService.perfMarkerEnd(marker);
        }
    }

    /**
     * Creates a time off request
     * @param tenantId
     * @param teamId
     * @param id
     * @param message
     * @param startTime
     * @param endTime
     * @param timeOffReasonId
     */
    public async createTimeOffRequest(
        tenantId: string,
        teamId: string,
        id: string,
        message: string,
        startTime: Moment,
        endTime: Moment,
        timeOffReasonId: string,
        requestedTimeOffBalance?: number
    ): Promise<IShiftRequestEntity> {
        let marker = "CreateTimeOffRequest";
        marker = InstrumentationService.perfMarkerStart(marker);

        try {
            let response: ICreateTimeOffResponseEntity;
            if (ECSConfigService.isECSFeatureEnabled(ECSConfigKey.TimeOffBalanceEnabled)) {
                response = await RestClient.createTimeOffRequest(
                    tenantId,
                    teamId,
                    id,
                    message,
                    startTime,
                    endTime,
                    timeOffReasonId,
                    requestedTimeOffBalance
                );
            } else {
                response = await RestClient.createTimeOffRequest(
                    tenantId,
                    teamId,
                    id,
                    message,
                    startTime,
                    endTime,
                    timeOffReasonId
                );
            }
            const shiftRequest: IShiftRequestEntity = response.shiftRequest;
            if (response.shift) {
                ShiftDataService.updateShiftsInStorage([response.shift], false /* isOptimisticUpdate */);
            }
            updateShiftRequestsInStore([shiftRequest]);
            return shiftRequest;
        } finally {
            InstrumentationService.perfMarkerEnd(marker);
        }
    }

    /**
     * Approve/Decline Time off request
     * @param tenantId
     * @param teamId
     * @param shiftRequestId
     * @param shiftReqeustETag
     * @param isAccepting
     * @param managerMessage
     */
    public async approveDeclineTimeOffRequest(
        tenantId: string,
        teamId: string,
        shiftRequestId: string,
        shiftReqeustETag: string,
        isAccepting: boolean,
        managerMessage?: string,
        approvedTimeOffBalance?: number
    ): Promise<IApproveDeclineTimeOffRequestResponseEntity> {
        let marker = "approveDenyShiftRequest";
        marker = InstrumentationService.perfMarkerStart(marker);
        try {
            const shiftRequestResponse: IApproveDeclineTimeOffRequestResponseEntity = await RestClient.approveDeclineTimeOffRequest(
                tenantId,
                teamId,
                shiftRequestId,
                shiftReqeustETag,
                managerMessage,
                isAccepting,
                approvedTimeOffBalance
            );
            if (shiftRequestResponse.shift) {
                ShiftDataService.updateShiftsInStorage([shiftRequestResponse.shift], false /* isOptimisticUpdate */);
                updateUnreadRequestsCountInStore(shiftRequestResponse.unreadShiftRequests);
            }
            if (shiftRequestResponse.shiftRequest) {
                updateShiftRequestsInStore([shiftRequestResponse.shiftRequest]);
            }
            return shiftRequestResponse;
        } finally {
            InstrumentationService.perfMarkerEnd(marker);
        }
    }

    /**
     * Approve the swap or handoff request
     * @param tenantId
     * @param teamId
     * @param shiftRequestId
     * @param shiftReqeustETag
     * @param isReceiver
     * @param responseMessage
     */
    public async approveSwapHandoffRequest(
        tenantId: string,
        teamId: string,
        shiftRequestId: string,
        shiftReqeustETag: string,
        isReceiver: boolean,
        responseMessage?: string
    ): Promise<IShiftRequestEntity> {
        let marker = "approveDenySwapHandOffRequest";
        marker = InstrumentationService.perfMarkerStart(marker);

        try {
            const response: IApproveSwapHandOffRequestResponseEntity = await RestClient.approveSwapHandOffRequest(
                tenantId,
                teamId,
                shiftRequestId,
                shiftReqeustETag,
                responseMessage,
                isReceiver
            );
            let updatedShiftRequest: IShiftRequestEntity = null;
            if (response) {
                ShiftDataService.updateShiftsInStorage(response.deletedShifts, false /* isOptimisticUpdate */);
                ShiftDataService.updateShiftsInStorage(response.replacementShifts, false /* isOptimisticUpdate */);
                updateShiftRequestsInStore(response.updatedShiftRequests);
                updateUnreadRequestsCountInStore(response.unreadShiftRequests);
                updatedShiftRequest = ShiftRequestUtils.getShiftRequestFromListById(response.updatedShiftRequests, shiftRequestId);
            }
            return updatedShiftRequest;
        } finally {
            InstrumentationService.perfMarkerEnd(marker);
        }
    }

    /**
     * Decline swap and handoff requests
     * @param tenantId
     * @param teamId
     * @param shiftRequestId
     * @param shiftReqeustETag
     * @param isReceiver
     * @param responseMessage
     */
    public async declineSwapHandoffRequest(
        tenantId: string,
        teamId: string,
        shiftRequestId: string,
        shiftReqeustETag: string,
        isReceiver: boolean,
        responseMessage?: string
    ): Promise<IShiftRequestEntity> {
        let marker = "denySwapHandOffRequest";
        marker = InstrumentationService.perfMarkerStart(marker);

        try {
            const response: IShiftRequestAndUnreadCountResponseEntity = await RestClient.declineSwapHandOffRequest(
                tenantId,
                teamId,
                shiftRequestId,
                shiftReqeustETag,
                responseMessage,
                isReceiver
            );
            let updatedShiftRequest: IShiftRequestEntity = null;
            if (response) {
                if (response.shiftRequest) {
                    updateShiftRequestsInStore([response.shiftRequest]);
                    updatedShiftRequest = response.shiftRequest;
                }
                updateUnreadRequestsCountInStore(response.unreadShiftRequests);
            }
            return updatedShiftRequest;
        } finally {
            InstrumentationService.perfMarkerEnd(marker);
        }
    }

    /**
     * Creates a swap or handoff request
     *
     * @param tenantId
     * @param teamId
     * @param id - shift request id
     * @param shiftId - Id of the shift being offered or swapped
     * @param otherShiftId - Id of the other shift for swap
     * @param receiverMemberId - Id of the member to send the request to
     * @param message - Message from the sender to the receiver
     * @param requestType - The type of request: "Swap" or "Handoff"
     */
    public async createSwapHandoffShiftRequest(
        tenantId: string,
        teamId: string,
        id: string,
        shiftId: string,
        otherShiftId: string,
        receiverMemberId: string,
        message: string,
        requestType: ShiftRequestType
    ): Promise<IShiftRequestEntity> {
        let marker = "CreateSwapHandoffShiftRequest";
        marker = InstrumentationService.perfMarkerStart(marker);

        try {
            const shiftRequest: IShiftRequestEntity = await RestClient.createSwapHandoffShiftRequest(
                tenantId,
                teamId,
                id,
                shiftId,
                otherShiftId,
                receiverMemberId,
                message,
                requestType
            );
            if (shiftRequest) {
                updateShiftRequestsInStore([shiftRequest]);
            }
            return shiftRequest;
        } finally {
            InstrumentationService.perfMarkerEnd(marker);
        }
    }

    /**
     * Approve/Decline multiple shift requests for an open shift.
     * @param teamId
     * @param openShift
     * @param shiftRequestsDetails
     */
    public async approveDeclineOpenShiftRequestsBulk(
        teamId: string,
        openShift: IOpenShiftEntity,
        shiftRequestsDetails: IShiftRequestResponseEntity[]
    ): Promise<IBulkOpenShiftRequestsResponseEntity> {
        let marker = "approveDeclineOpenShiftRequestsBulk";
        marker = InstrumentationService.perfMarkerStart(marker);

        try {
            const shiftRequestsResponse: IBulkOpenShiftRequestsResponseEntity = await RestClient.bulkAcceptDeclineOpenShifts(
                teamId,
                openShift.id,
                shiftRequestsDetails
            );
            // Update shifts created in cache and the unread shift request count
            if (shiftRequestsResponse.createdShifts) {
                ShiftDataService.updateShiftsInStorage(shiftRequestsResponse.createdShifts, false /* isOptimisticUpdate */);
            }

            updateUnreadRequestsCountInStore(shiftRequestsResponse.unreadShiftRequestsCount);

            // Update OpenShift in store
            if (shiftRequestsResponse.updatedOpenShift) {
                ShiftDataService.updateOpenShiftsInStorage([shiftRequestsResponse.updatedOpenShift], false /* isOptimisticUpdate */);
            }

            // Update the openshift requests
            if (shiftRequestsResponse.updatedOpenShiftRequests) {
                updateShiftRequestsInStore(shiftRequestsResponse.updatedOpenShiftRequests);
            }

            return shiftRequestsResponse;
        } catch (error) {
            throw error;
        } finally {
            InstrumentationService.perfMarkerEnd(marker);
        }
    }

    /**
     * Requests given open shift.
     * @param openShift The open shift to request.
     * @returns The created shift request.
     */
    public async requestOpenShift(openShift: IOpenShiftEntity): Promise<IShiftRequestEntity> {
        if (!openShift) {
            return null;
        }

        // currently we are not allowing use of the message field
        const message = "";
        const shiftRequestId = ShiftRequestEntity.generateNewShiftRequestId();
        const selectedTeamId = TeamStore().teamId;
        let senderTeamId: string | undefined = undefined;
        let marker = "requestOpenShift";

        marker = InstrumentationService.perfMarkerStart(marker);

        if (openShift.isCrossLocationOpenShift) {
            senderTeamId = selectedTeamId;
        }

        try {
            const request = await RestClient.requestOpenShift(
                openShift.tenantId,
                openShift.teamId,
                openShift.id,
                shiftRequestId,
                message,
                senderTeamId
            );

            updateShiftRequestsInStore([request]);
            return request;
        } finally {
            InstrumentationService.perfMarkerEnd(marker);
        }
    }

    /**
     * Returns all the available time off reasons for the current user.
     * @returns {} Promise that returns a filtered set of time off reasons that the current user is eligible to use.
     */
    public async getRequestableTimeOffReasons(): Promise<IUserTimeOffReasonsReponseEntity> {
        let marker = "getRequestableTimeOffReasons";
        marker = InstrumentationService.perfMarkerStart(marker);
        const { id } = TeamDataService.getCurrentTeam();

        try {
            const timeOffReasons = await RestClient.getRequestableTimeOffReasons(id);

            return timeOffReasons;
        } finally {
            InstrumentationService.perfMarkerEnd(marker);
        }
    }

    /**
     * Returns all the memberShifts for a specified time range
     * @param userId User Id of the member to retrieve the shifts for
     * @param teamId Teams Id for that member
     * @param startTimes start time of the shifts to retrieve
     * @param endTime end time of the shifts to retrieve
     */
    public async getShiftsInDateRangeForUserId(
        memberId: string,
        teamId: string,
        startTime: Moment,
        endTime: Moment
    ): Promise<IShiftEntity[]> {
        if (!memberId && !teamId) {
            return null;
        }

        let marker = "getShiftsInDateRangeForUserId";
        marker = InstrumentationService.perfMarkerStart(marker);
        const member = TeamStore() && TeamStore().members.get(memberId);

        try {
            if (member) {
                const memberShifts: IShiftEntity[] = await RestClient.getShiftsInDateRangeForUser(
                    member.userId,
                    teamId,
                    startTime,
                    endTime
                );
                // update the member shifts in conflict store
                setMemberShiftsInStore(memberShifts);
                return memberShifts;
            }
        } finally {
            InstrumentationService.perfMarkerEnd(marker);
        }
    }

    /**
     * Resets the sync state
     */
    public async resetSyncState() {}
}

const service: IShiftRequestDataService = new ShiftRequestDataService();
export default service;